import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { Input } from '../index';

/**
 * AgentPickerDropdown — anchored, searchable, paginated agent picker.
 *
 * Replaces the raw native <select> menus on the Live Trust Action tab, which
 * dumped hundreds of agents into one unscrollable, unsearchable list (and the
 * Top-10 menu showed agent IDs). This is an anchored dropdown (not a modal —
 * operators have rejected modal overlays for the V2 surfaces) with a search
 * field and pagination so any agent is findable in a few keystrokes.
 *
 * @param {string}   props.label    — trigger text (e.g. "+ Add agent…")
 * @param {Array}    props.items     — [{ id, name, sub }] (sub = optional muted hint)
 * @param {Function} props.onPick    — (id, name) => void
 * @param {boolean}  [props.disabled]
 * @param {number}   [props.pageSize=8]
 * @param {number}   [props.minWidth=190]
 */
export function AgentPickerDropdown({
  label = '+ Add agent…',
  items = [],
  onPick,
  disabled = false,
  pageSize = 8,
  minWidth = 190,
  align = 'left',
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const ref = useRef(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Reset paging whenever the query changes.
  useEffect(() => { setPage(0); }, [q]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((it) =>
      String(it.name || '').toLowerCase().includes(needle)
      || String(it.sub || '').toLowerCase().includes(needle)
      || String(it.id || '').toLowerCase().includes(needle));
  }, [items, q]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const slice = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);

  const pick = (it) => {
    onPick?.(it.id, it.name);
    setOpen(false);
    setQ('');
    setPage(0);
  };

  return (
    <div className="relative inline-block" ref={ref} style={{ minWidth }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="w-full inline-flex items-center justify-between gap-2 border border-v2-border rounded-v2-md px-2.5 h-7 bg-v2-surface text-[12px] hover:border-v2-border-strong disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="truncate text-v2-ink-secondary">{label}</span>
        <ChevronDown size={13} className="text-v2-ink-muted shrink-0" />
      </button>

      {open && (
        <div
          className={`absolute z-50 mt-1 w-[300px] max-w-[92vw] bg-v2-surface border border-v2-border rounded-v2-md shadow-v2-md overflow-hidden ${align === 'right' ? 'right-0' : 'left-0'}`}
        >
          <div className="p-2 border-b border-v2-border-subtle">
            <div className="relative">
              <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-v2-ink-muted pointer-events-none" />
              <Input
                size="sm"
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search agents…"
                className="pl-7"
              />
            </div>
          </div>

          <ul className="m-0 p-0 list-none max-h-[260px] overflow-y-auto">
            {slice.length === 0 ? (
              <li className="px-3 py-4 text-[12px] text-v2-ink-muted text-center">
                {items.length === 0 ? 'No agents available.' : `No agents match “${q}”.`}
              </li>
            ) : slice.map((it) => (
              <li key={it.id}>
                <button
                  type="button"
                  onClick={() => pick(it)}
                  className="w-full text-left px-3 py-2 hover:bg-v2-action-soft flex items-center justify-between gap-2"
                >
                  <span className="text-[12px] text-v2-ink truncate">{it.name}</span>
                  {it.sub != null && it.sub !== '' && (
                    <span className="text-[10.5px] text-v2-ink-muted font-mono-v2 shrink-0">{it.sub}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>

          {filtered.length > pageSize && (
            <div className="flex items-center justify-between px-2 py-1.5 border-t border-v2-border-subtle text-[10.5px] text-v2-ink-muted">
              <span>{filtered.length} agents</span>
              <span className="inline-flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={safePage <= 0}
                  onClick={() => setPage(safePage - 1)}
                  className="px-1.5 py-0.5 rounded-v2-sm disabled:opacity-40 enabled:hover:bg-v2-muted/60"
                  aria-label="Previous page"
                >‹</button>
                <span>{safePage + 1} / {pageCount}</span>
                <button
                  type="button"
                  disabled={safePage >= pageCount - 1}
                  onClick={() => setPage(safePage + 1)}
                  className="px-1.5 py-0.5 rounded-v2-sm disabled:opacity-40 enabled:hover:bg-v2-muted/60"
                  aria-label="Next page"
                >›</button>
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AgentPickerDropdown;
